import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import OrderedPartForm from "../../components/service/orderedPartForm.component";
import { useCRUD } from "../../api/crud";
import { ORDERED_PARTS_ENDPOINT } from "../../consts";
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";

export const OrderedPartDetailsActions = () => {
  const { id, orderedPartId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const orderedPartIdToUse = orderedPartId || id;
  const { loading, error, remove } = useCRUD(ORDERED_PARTS_ENDPOINT);
  const [updateOrderedPartOpened, setUpdateOrderedPartOpened] = useState<boolean>(false);
  const [deleteOrderedPartOpened, setDeleteOrderedPartOpened] = useState<boolean>(false);
  const canChangeOrderedPart = useHasPermission("service.change_orderedpart");
  const canDeleteOrderedPart = useHasPermission("service.delete_orderedpart");

  const handleDeleteOrderedPart = async () => {
    if (loading || !orderedPartIdToUse) {
      return;
    }

    const result = await remove({ id: orderedPartIdToUse });

    if (result?.success) {
      enqueueSnackbar(t('Ordered part deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteOrderedPartOpened(false);
      if (id) {
        navigate(`/service/stock-status/${id}/ordered-parts`);
      } else {
        navigate('/service/stock-status');
      }
    } else {
      enqueueSnackbar(t('Failed to delete ordered part'), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t("Update ordered part"),
      onClick: () => {
        setUpdateOrderedPartOpened(true);
      },
      disabled: !canChangeOrderedPart,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete ordered part"),
      onClick: () => {
        setDeleteOrderedPartOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeleteOrderedPart,
    },
  ];

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this ordered part will permanently remove it from the system. This action cannot be undone.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteOrderedPart}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateOrderedPartOpened}
        setOpened={setUpdateOrderedPartOpened}
        title={t("Update ordered part")}
        content={
          <OrderedPartForm
            id={orderedPartIdToUse}
            initialPartId={id}
            onSuccess={() => {
              setUpdateOrderedPartOpened(false);
              window.dispatchEvent(new Event("orderedPartUpdated"));
              window.dispatchEvent(new Event("partUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={deleteOrderedPartOpened}
        setOpened={setDeleteOrderedPartOpened}
        title={t("Delete ordered part")}
        content={deleteModalContent}
      />
    </>
  );
};
